import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Card, 
  CardContent,
  InputAdornment,
  IconButton,
  Container,
  Link,
  Avatar
} from '@mui/material';
import { Visibility, VisibilityOff, MedicalServices } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { setEmail as setAuthEmail } from '../utils/auth';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const navigate = useNavigate();

  // Simple validation for the email format
  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = re.test(email);
    if (!isValid) {
      setEmailError('Bitte geben Sie eine gültige E-Mail-Adresse ein');
    } else {
      setEmailError('');
    }
    return isValid;
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    // For gating editor access we only validate email format; password is ignored
    if (validateEmail(email)) {
      setAuthEmail(email);
      navigate('/overview');
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Card
          sx={{
            width: '100%',
            maxWidth: 450,
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
          }}
        >
          <CardContent sx={{ padding: 4 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Avatar
                sx={{
                  bgcolor: 'primary.main',
                  width: 56,
                  height: 56,
                  mb: 2,
                }}
              >
                <MedicalServices fontSize="large" />
              </Avatar>
              <Typography component="h1" variant="h4" fontWeight="500" gutterBottom>
                PerTRAIN
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Melden Sie sich an, um auf Ihre Patientenkommunikation zuzugreifen
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleLogin} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="E-Mail-Adresse"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!emailError}
                helperText={emailError}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="normal"
                fullWidth
                name="password"
                label="Passwort (optional)"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ 
                  mt: 2, 
                  mb: 2, 
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Anmelden
              </Button>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link href="#" variant="body2">
                  Passwort vergessen?
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Für die Demo reicht eine gültige E‑Mail. Passwort ist optional. Editor-Funktionen sind nur für eine freigeschaltete E‑Mail sichtbar.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default LoginPage;
